import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  integrationSchema,
  integrationStatusCountsSchema,
  integrationsPageSchema,
  type IntegrationMutationPayload,
  type IntegrationsFilter,
  IntegrationMappingType,
  IntegrationStatus,
} from './model';

interface IntegrationsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const integrationMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.union([z.void(), z.looseObject({ id: z.coerce.string().optional() })]),
);
const mappingUploadResponseSchema = z.number().int().nonnegative();
const mappingPathByType = {
  [IntegrationMappingType.Bundle]: 'bundle',
  [IntegrationMappingType.Domain]: 'domain',
} as const;

export function createIntegrationsClient({
  config,
  accessToken,
  fetcher,
}: IntegrationsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    list: (filter: IntegrationsFilter, options?: { signal?: AbortSignal }) =>
      api.request({
        path: `/api/dps/integrations?${serializeIntegrationsFilter(filter)}`,
        schema: integrationsPageSchema,
        ...(options?.signal ? { init: { signal: options.signal } } : {}),
      }),
    listForPartner: (demandPartnerId: string, filter: IntegrationsFilter) =>
      api.request({
        path: `/api/dps/${encodeURIComponent(demandPartnerId)}/integrations?${serializeIntegrationsFilter(filter)}`,
        schema: integrationsPageSchema,
      }),
    listByIds: (ids: string[]) =>
      api.request({
        path: `/api/dps/integrations?${toSearchParams({
          name: '',
          status: Object.values(IntegrationStatus),
          ids,
          size: ids.length,
          page: 0,
        })}`,
        schema: integrationsPageSchema,
      }),
    downloadMappingTemplate: (type: IntegrationMappingType) =>
      api.requestBlob({
        path: `/api/dps/integrations/csv/export-${mappingPathByType[type]}-mappings-template`,
      }),
    uploadMappings: (integrationId: string, type: IntegrationMappingType, file: File) => {
      const body = new FormData();
      body.append('file', file);
      return api.request({
        path: `/api/dps/integrations/${encodeURIComponent(integrationId)}/csv/import-${mappingPathByType[type]}-mappings`,
        schema: mappingUploadResponseSchema,
        init: { method: 'POST', body },
      });
    },
    getStatusCounts: (filter: Pick<IntegrationsFilter, 'searchTerm'>) =>
      api.request({
        path: `/api/dps/integrations/statuses?${toSearchParams({
          name: filter.searchTerm || undefined,
        })}`,
        schema: integrationStatusCountsSchema,
      }),
    getStatusCountsForPartner: (
      demandPartnerId: string,
      filter: Pick<IntegrationsFilter, 'searchTerm'>,
    ) =>
      api.request({
        path: `/api/dps/integrations/statuses?${toSearchParams({
          dpId: demandPartnerId,
          name: filter.searchTerm || undefined,
        })}`,
        schema: integrationStatusCountsSchema,
      }),
    get: (demandPartnerId: string, integrationId: string) =>
      api.request({
        path: `/api/dps/${encodeURIComponent(demandPartnerId)}/integrations/${encodeURIComponent(integrationId)}`,
        schema: integrationSchema,
      }),
    create: (demandPartnerId: string, payload: IntegrationMutationPayload) =>
      api.request({
        path: `/api/dps/${encodeURIComponent(demandPartnerId)}/integrations`,
        schema: integrationMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    update: (demandPartnerId: string, integrationId: string, payload: IntegrationMutationPayload) =>
      api.request({
        path: `/api/dps/${encodeURIComponent(demandPartnerId)}/integrations/${encodeURIComponent(integrationId)}`,
        schema: integrationMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
  };
}

export function serializeIntegrationsFilter(filter: IntegrationsFilter) {
  return toSearchParams({
    name: filter.searchTerm,
    status: filter.statuses,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}

export type IntegrationsClient = ReturnType<typeof createIntegrationsClient>;
